/**
 * Runtime CSS property → keyword completions via css-tree's lexer.
 *
 * Queries css-tree's W3C syntax grammars lazily at runtime instead of
 * shipping a large generated map. Results are cached per-property —
 * each property is computed once on first access, then served from cache.
 *
 * css-tree is already in the client bundle (used by the style validation
 * system), so this adds zero additional bundle weight.
 */

import type {Completion} from '@codemirror/autocomplete';
import {lexer} from 'css-tree';

/** CSS global values valid for any property. */
export const CSS_GLOBAL_VALUES: readonly string[] = [
  'inherit',
  'initial',
  'revert',
  'revert-layer',
  'unset',
];

/**
 * Maximum keyword values before we skip a property.
 * The largest standard property is `content` (~254 keywords); color-related
 * properties have ~179 (named + system colors). 300 covers all known
 * properties with headroom. Properties exceeding this (none currently)
 * fall through to CM6's built-in completion source.
 */
const MAX_KEYWORDS = 300;

const GLOBAL_SET = new Set<string>(CSS_GLOBAL_VALUES);

/** Lazy cache: property → completions array, or null if skipped. */
const cache = new Map<string, readonly Completion[] | null>();

// Narrow type for internal lexer APIs not fully exposed in @types/css-tree.
interface CssTreeLexerInternal {
  getProperty(name: string): {syntax?: unknown} | undefined;
  types: Record<string, {syntax?: unknown}>;
  properties: Record<string, {syntax?: unknown}>;
}

const cssLexer = lexer as unknown as CssTreeLexerInternal;

/**
 * Walk a css-tree syntax node and collect all Keyword terminals.
 * Handles recursive Type/Property references with cycle detection.
 */
function extractKeywords(propertyName: string): string[] {
  const keywords = new Set<string>();
  const visited = new Set<string>();

  function walk(syntax: any): void {
    if (!syntax) return;

    if (syntax.type === 'Keyword') {
      if (
        syntax.name &&
        !syntax.name.startsWith('-') &&
        !GLOBAL_SET.has(syntax.name)
      ) {
        keywords.add(syntax.name);
      }
      return;
    }

    // Dedup Type/Property references to avoid infinite recursion
    if (syntax.type === 'Type' || syntax.type === 'Property') {
      const key = syntax.type + ':' + syntax.name;
      if (visited.has(key)) return;
      visited.add(key);

      const resolved =
        syntax.type === 'Type'
          ? cssLexer.types[syntax.name]
          : cssLexer.properties[syntax.name];
      if (resolved?.syntax) {
        walk(resolved.syntax);
      }
      return;
    }

    // Recurse into compound nodes (Group, Multiplier, etc.)
    if (syntax.terms) {
      for (const term of syntax.terms) {
        walk(term);
      }
    }
    if (syntax.term) {
      walk(syntax.term);
    }
  }

  const prop = cssLexer.getProperty(propertyName);
  if (prop?.syntax) {
    walk(prop.syntax);
  }

  return [...keywords].sort();
}

/**
 * Get Completion objects for a CSS property's valid keyword values.
 * Returns null for unknown properties, or those with no/too many keywords.
 * First call per property computes from css-tree; subsequent calls are O(1).
 */
export function getCssPropertyCompletions(
  property: string
): readonly Completion[] | null {
  const cached = cache.get(property);
  if (cached !== undefined) return cached;

  const keywords = extractKeywords(property);
  if (keywords.length === 0 || keywords.length > MAX_KEYWORDS) {
    cache.set(property, null);
    return null;
  }

  const completions: readonly Completion[] = keywords.map((label) => ({
    label,
    type: 'keyword' as const,
  }));
  cache.set(property, completions);
  return completions;
}

/** Check whether a property has keyword completions available. */
export function hasCssPropertyCompletions(property: string): boolean {
  return getCssPropertyCompletions(property) !== null;
}
